import { createFileRoute } from "@tanstack/react-router";
import { CompactNoteForm } from "@/components/compact-note-form";
import { useQuery } from "@tanstack/react-query";
import { homeNotesQueryOptions } from "@/queries/home";
import { Star, Calendar, Clock, AlertTriangle, List } from "lucide-react";
import { useMemo } from "react";
import { useDocumentTitle } from "usehooks-ts";

import { useNoteCreation } from "@/hooks/useNoteCreation";
import { OverdueSection } from "@/components/home/overdue-section";
import { DueTodaySection } from "@/components/home/due-today-section";
import {
  TodayEmptyState,
  ThisEveningSection,
  UpcomingSection,
  DueListsSection,
} from "@/components/today";
import { OverdueFilterDropdown } from "@/components/today/overdue-filter-dropdown";
import { useOverdueFilter } from "@/hooks/use-overdue-filter";
import { PageHeader } from "@/components/layout/page-header";
import { UnauthenticatedPage } from "@/components/layout/unauthenticated-page";
import type { NoteTypes } from "db/types";

export const Route = createFileRoute("/today")({
  component: TodayView,
});

type Note = NoteTypes.SerializedNote;

function TodayView() {
  const { user } = Route.useRouteContext();
  useDocumentTitle("Today | Notebook");

  // Use optimized home notes query with server-side filtering
  const { data: homeNotes } = useQuery({
    ...homeNotesQueryOptions(user?.id),
    enabled: !!user?.id,
  });

  const { handleNoteCreated, isPending: isCreateNotePending } = useNoteCreation(
    user?.id ?? "",
  );

  const {
    excludedTypes,
    toggleType,
    isTypeIncluded,
    hasActiveFilters,
    allIncluded,
    includeAll,
    excludeAll,
  } = useOverdueFilter();

  // Use server-side filtered data from home notes
  const {
    overdueNotes = [],
    dueTodayNotes = [],
    overdueLists = [],
    dueTodayLists = [],
    upcomingLists = [],
  } = user?.id && homeNotes
    ? homeNotes
    : {
        overdueNotes: [],
        dueTodayNotes: [],
        overdueLists: [],
        dueTodayLists: [],
        upcomingLists: [],
      };

  const filteredOverdueNotes = useMemo(() => {
    return overdueNotes.filter((note: Note) => isTypeIncluded(note.type || ""));
  }, [overdueNotes, isTypeIncluded]);

  const filteredDueTodayNotes = useMemo(() => {
    return dueTodayNotes.filter((note: Note) =>
      isTypeIncluded(note.type || ""),
    );
  }, [dueTodayNotes, isTypeIncluded]);

  const todayNotes = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
    const thisEveningStart = new Date(
      todayStart.getTime() + 18 * 60 * 60 * 1000,
    );

    const sections = {
      overdue: filteredOverdueNotes as Note[],
      today: [] as Note[],
      this_evening: [] as Note[],
      upcoming: [] as Note[],
    };

    filteredDueTodayNotes.forEach((note: Note) => {
      const dueDate = note.dueDate ? new Date(note.dueDate) : null;

      if (!dueDate) return;

      if (dueDate >= todayStart && dueDate < thisEveningStart) {
        sections.today.push(note);
      } else if (dueDate >= thisEveningStart && dueDate < todayEnd) {
        sections.this_evening.push(note);
      } else if (dueDate >= todayEnd) {
        sections.upcoming.push(note);
      }
    });

    return sections;
  }, [filteredOverdueNotes, filteredDueTodayNotes]);

  if (!user?.id) {
    return (
      <UnauthenticatedPage
        title="Today"
        description="Sign in to see your scheduled tasks"
        icon={Calendar}
        iconColor="text-yellow-500"
      />
    );
  }

  const hasContent =
    filteredOverdueNotes.length > 0 ||
    todayNotes.today.length > 0 ||
    todayNotes.this_evening.length > 0 ||
    todayNotes.upcoming.length > 0 ||
    overdueLists.length > 0 ||
    dueTodayLists.length > 0 ||
    upcomingLists.length > 0;

  return (
    <>
      <PageHeader
        title="Today"
        icon={Star}
        iconColor="text-yellow-500"
        actions={
          <OverdueFilterDropdown
            excludedTypes={excludedTypes}
            onToggleType={toggleType}
            isTypeIncluded={isTypeIncluded}
            hasActiveFilters={hasActiveFilters}
            allIncluded={allIncluded}
            onIncludeAll={includeAll}
            onExcludeAll={excludeAll}
          />
        }
      />
      <>
        <div className="animate-fade-in max-w-4xl mx-auto w-full mb-6 md:mb-8 py-3 md:py-4 px-4">
          <CompactNoteForm
            onSubmit={handleNoteCreated}
            isSubmitting={isCreateNotePending}
          />
        </div>

        <div className="flex-1 space-y-6 md:space-y-8 w-full max-w-4xl mx-auto min-h-screen px-4 overflow-x-hidden">
          {/* Today Sections */}
          <OverdueSection notes={filteredOverdueNotes} />
          <DueListsSection
            lists={overdueLists}
            title="Overdue Lists"
            icon={
              <AlertTriangle className="h-4 md:h-5 w-4 md:w-5 text-red-500" />
            }
          />
          <DueTodaySection notes={todayNotes.today} />
          <DueListsSection
            lists={dueTodayLists}
            title="Lists Due Today"
            icon={<List className="h-4 md:h-5 w-4 md:w-5 text-blue-500" />}
          />
          <ThisEveningSection notes={todayNotes.this_evening} />
          <UpcomingSection notes={todayNotes.upcoming} />
          <DueListsSection
            lists={upcomingLists}
            title="Upcoming Lists"
            icon={
              <Clock className="h-4 md:h-5 w-4 md:w-5 text-muted-foreground" />
            }
          />

          {/* Empty State */}
          {!hasContent && <TodayEmptyState />}
        </div>
      </>
    </>
  );
}
